import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { ActivityLive, ActivitySessions, ActivityAudience, ActivityConnections } from './ActivityTabs';
import { ReviewsPanel } from './ReviewsPanel';
import { ContentStatsTable } from './ContentStatsTable';

export function LoadingSpinner() {
  return <SharedLoadingSpinner />;
}

export function StatCard({ icon: Icon, label, value, sub, color = 'text-[#E9641A]', bgColor = 'bg-[#E9641A]/10', theme }) {
  const light = theme === 'light';
  return (
    <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center`}><Icon size={18} className={color} /></div>
        <span className={`text-xs uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/45'}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className={`text-xs mt-1 ${light ? 'text-gray-400' : 'text-white/30'}`}>{sub}</p>}
    </div>
  );
}

/* ─── DASHBOARD ─── */
export const PERIOD_OPTS = [
  { v: 0, l: 'Tout' },
  { v: 7, l: '7 j' },
  { v: 30, l: '30 j' },
  { v: 90, l: '90 j' },
];

export function PeriodFilter({ days, setDays, light }) {
  return (
    <div className={`flex rounded-lg p-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`} data-testid="stats-period-filter">
      {PERIOD_OPTS.map(p => (
        <button key={p.v} data-testid={`stats-period-${p.v}`} onClick={() => setDays(p.v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            days === p.v ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white'
          }`}>
          {p.l}
        </button>
      ))}
    </div>
  );
}

function AdsHealthBanner({ light }) {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/ads/health`, { withCredentials: true }).then(r => setHealth(r.data)).catch(() => {});
  }, []);
  if (!health || !health.plans_with_ads) return null;
  const issues = [];
  if (health.active_midroll === 0) issues.push({ critical: true, text: 'Aucune publicité « milieu de film » active — les coupures pub ne se déclenchent plus pour les abonnés avec pub.' });
  if (health.active_preroll === 0) issues.push({ critical: true, text: 'Aucune publicité « début de film » active.' });
  (health.expiring || []).forEach(a => issues.push({
    critical: a.days_left <= 1,
    text: `La publicité « ${a.name} » expire ${a.days_left <= 0 ? "aujourd'hui" : `dans ${a.days_left} jour${a.days_left > 1 ? 's' : ''}`} (${a.end_date}).`,
  }));
  if (!issues.length) return null;
  const critical = issues.some(i => i.critical);
  return (
    <div className={`mb-6 px-4 py-3 rounded-xl border ${critical
      ? (light ? 'bg-red-50 border-red-200' : 'bg-red-500/8 border-red-500/25')
      : (light ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/8 border-amber-500/25')}`} data-testid="ads-health-banner">
      <p className={`text-xs font-bold flex items-center gap-2 mb-1 ${critical ? 'text-red-500' : 'text-amber-500'}`}>
        <AlertTriangle size={14} /> Publicités : action requise
      </p>
      <ul className="space-y-0.5">
        {issues.map((i, k) => (
          <li key={k} className={`text-xs ${light ? 'text-gray-600' : 'text-white/60'}`} data-testid={`ads-health-issue-${k}`}>• {i.text}</li>
        ))}
      </ul>
    </div>
  );
}

export function DashboardTab({ analytics, viewsData, theme, statsDays = 0, setStatsDays }) {
  const light = theme === 'light';
  const [sub, setSub] = useState('overview');
  if (!analytics) return <LoadingSpinner />;
  const periodLabel = statsDays > 0 ? `${statsDays} derniers jours` : 'Depuis le début';
  const chartData = viewsData.slice(-(statsDays > 0 ? statsDays : 30));
  const maxV = Math.max(1, ...chartData.map(d => d.views));
  const maxU = Math.max(1, ...chartData.map(d => d.users));
  return (
    <div data-testid="tab-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Dashboard</h1>
        {sub === 'overview' && setStatsDays && <PeriodFilter days={statsDays} setDays={setStatsDays} light={light} />}
      </div>
      <div className={`flex gap-1 mb-6 rounded-lg p-1 w-fit flex-wrap ${light ? 'bg-gray-100' : 'bg-white/5'}`} data-testid="dashboard-subtabs">
        {[['overview', 'Vue d\'ensemble'], ['live', 'En direct'], ['connections', 'Connexions'], ['sessions', 'Sessions'], ['audience', 'Audience'], ['contents', 'Contenus'], ['reviews', 'Avis']].map(([id, lbl]) => (
          <button key={id} data-testid={`dash-sub-${id}`} onClick={() => setSub(id)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              sub === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-900' : 'text-white/50 hover:text-white'}`}>
            {lbl}{id === 'live' && sub !== 'live' && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
          </button>
        ))}
      </div>
      {sub === 'overview' && (<>
      <AdsHealthBanner light={light} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <StatCard icon={Users} label="Utilisateurs" value={analytics.total_users} color="text-blue-400" bgColor="bg-blue-500/10" theme={theme} />
        <StatCard icon={UserCheck} label="Actifs" value={analytics.active_users} color="text-green-400" bgColor="bg-green-500/10" sub="Temps réel" theme={theme} />
        <StatCard icon={Film} label="Contenus" value={analytics.total_contents} sub={`${analytics.total_movies} films · ${analytics.total_series} séries`} theme={theme} />
        <StatCard icon={Eye} label="Vues" value={analytics.total_views} color="text-purple-400" bgColor="bg-purple-500/10" sub={periodLabel} theme={theme} />
        <StatCard icon={TrendingUp} label="Rétention" value="78%" color="text-teal-400" bgColor="bg-teal-500/10" sub="Moy. 30j" theme={theme} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><TrendingUp size={16} className="text-[#E9641A]" /> Croissance ({statsDays > 0 ? `${statsDays}j` : '30j'})</h3>
          <div className="flex items-end gap-[3px] h-36">
            {chartData.map((d, i) => (
              <div key={i} className="flex-1 h-full flex flex-col justify-end gap-[1px]" title={`${d.date} · ${d.views} vue${d.views > 1 ? 's' : ''} · ${d.users} user${d.users > 1 ? 's' : ''}`}>
                <div className="bg-blue-500/40 rounded-t" style={{ height: `${Math.max(2, (d.users / maxU) * 45)}%` }} />
                <div className="bg-[#E9641A]/60 rounded-t" style={{ height: `${Math.max(2, (d.views / maxV) * 45)}%` }} />
              </div>
            ))}
          </div>
          <div className={`flex gap-4 mt-2 text-[10px] ${light ? 'text-gray-400' : 'text-white/30'}`}>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#E9641A]/40 rounded" />Vues</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500/30 rounded" />Users</span>
          </div>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Layers size={16} className="text-[#E9641A]" /> Par catégorie</h3>
          <div className="space-y-2.5">
            {Object.entries(analytics.category_stats || {}).map(([cat, count]) => (
              <div key={cat} className="flex items-center gap-3">
                <span className={`text-xs capitalize w-24 truncate ${light ? 'text-gray-500' : 'text-white/50'}`}>{cat}</span>
                <div className={`flex-1 h-2 rounded-full ${light ? 'bg-gray-100' : 'bg-white/5'}`}><div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${(count / analytics.total_contents) * 100}%` }} /></div>
                <span className={`text-xs w-6 text-right ${light ? 'text-gray-400' : 'text-white/35'}`}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Top contenus</h3>
        {analytics.popular_contents?.slice(0, 8).map((c, i) => (
          <div key={i} className={`flex items-center gap-3 py-2 text-sm border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
            <span className={`w-5 text-right font-mono text-xs ${light ? 'text-gray-300' : 'text-white/25'}`}>{i + 1}</span>
            <span className={`flex-1 truncate text-xs ${light ? 'text-gray-700' : 'text-white'}`}>{c.title}</span>
            <span className={`text-xs ${light ? 'text-gray-400' : 'text-white/35'}`}>{(c.views || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
      </>)}
      {sub === 'live' && <ActivityLive theme={theme} />}
      {sub === 'connections' && <ActivityConnections theme={theme} />}
      {sub === 'sessions' && <ActivitySessions theme={theme} />}
      {sub === 'audience' && <ActivityAudience theme={theme} />}
      {sub === 'reviews' && <ReviewsPanel theme={theme} />}
      {sub === 'contents' && <ContentStatsTable theme={theme} />}
    </div>
  );
}


/* ─── COUNTRY PICKER (imported from shared) ─── */

/* ─── MEDIA SPECS (imported from shared) ─── */

/* ─── CONTENT FORM ─── */
export function AiFilterAnalyze({ contentId, videoUrl, light, onDone }) {
  const [st, setSt] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const timer = useRef(null);
  const stopPoll = () => { clearInterval(timer.current); timer.current = null; };
  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-filters/status/${contentId}`, { withCredentials: true });
      setSt(data);
      if (['done', 'error', 'none'].includes(data.status)) stopPoll();
    } catch { stopPoll(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);
  useEffect(() => {
    if (!contentId) return;
    axios.get(`${API}/admin/ai-filters/status/${contentId}`, { withCredentials: true })
      .then(({ data }) => { setSt(data); if (['queued', 'running'].includes(data.status)) { stopPoll(); timer.current = setInterval(poll, 4000); } })
      .catch(() => {});
    return stopPoll;
  }, [contentId, poll]);
  const start = async () => {
    if (!window.confirm("Lancer l'analyse IA ? Le système va visionner et écouter tout le contenu pour détecter les scènes à filtrer (consomme du budget de la clé LLM).")) return;
    try {
      await axios.post(`${API}/admin/ai-filters/analyze`, { content_id: contentId }, { withCredentials: true });
      setSt({ status: 'queued', progress: 0, step: 'En file d\'attente…' });
      stopPoll();
      timer.current = setInterval(poll, 4000);
    } catch (e) { alert(e.response?.data?.detail || "Erreur au lancement de l'analyse"); }
  };
  const cancel = async () => {
    if (!window.confirm("Annuler l'analyse IA en cours ?")) return;
    try {
      await axios.post(`${API}/admin/ai-filters/cancel`, { content_id: contentId }, { withCredentials: true });
      stopPoll();
      setSt({ status: 'cancelled', step: 'Analyse annulée' });
    } catch (e) { alert(e.response?.data?.detail || "Erreur à l'annulation"); }
  };
  const busy = st?.status === 'running' || st?.status === 'queued';
  const pendingReview = st?.status === 'done' && !st?.reviewed;
  return (
    <div className="flex items-center gap-2" data-testid="ai-filter-analyze">
      {showTimeline && (
        <FilterTimelineEditor contentId={contentId} onClose={() => setShowTimeline(false)}
          onValidated={(filters) => { onDone(filters); setSt(s => ({ ...s, reviewed: true })); }} />
      )}
      {busy ? (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] ${light ? 'bg-purple-50 text-purple-700' : 'bg-purple-500/10 text-purple-300'}`} data-testid="ai-filter-progress">
          <Sparkles size={11} className="animate-pulse" />
          <span>{st.status === 'queued' ? `En file d'attente${st.queue_position ? ` (position ${st.queue_position})` : ''}…` : (st.step || 'Analyse en cours…')}</span>
          {st.status === 'running' && (
            <>
              <div className={`w-20 h-1 rounded-full ${light ? 'bg-purple-100' : 'bg-white/10'}`}>
                <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${st.progress || 0}%` }} />
              </div>
              <span className="font-semibold">{st.progress || 0}%</span>
            </>
          )}
          <button type="button" onClick={cancel} title="Annuler l'analyse"
            className={`ml-1 p-0.5 rounded transition-colors ${light ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/15'}`}
            data-testid="ai-filter-cancel-btn">
            <X size={12} />
          </button>
        </div>
      ) : (
        <>
          {pendingReview && <span className={`text-[9px] font-bold ${light ? 'text-orange-600' : 'text-orange-400'}`} data-testid="ai-filter-pending">⚠ {st.filters_found ?? 0} détection(s) IA à valider</span>}
          {st?.status === 'done' && st?.reviewed && <span className={`text-[9px] ${light ? 'text-green-600' : 'text-green-400'}`} data-testid="ai-filter-done">✓ Filtres validés</span>}
          {st?.status === 'error' && <span className="text-[9px] text-red-400" title={st.error} data-testid="ai-filter-error">Échec analyse</span>}
          {st?.status === 'cancelled' && <span className={`text-[9px] ${light ? 'text-gray-400' : 'text-white/40'}`} data-testid="ai-filter-cancelled">Analyse annulée</span>}
          {contentId && (
            <button type="button" onClick={() => setShowTimeline(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold rounded-lg transition-colors ${pendingReview ? 'bg-orange-500/15 text-orange-500 hover:bg-orange-500/25' : light ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/8 text-white/70 hover:bg-white/15'}`}
              title="Ouvrir la timeline de vérification des filtres"
              data-testid="open-timeline-btn">
              <SlidersHorizontal size={10} /> Vérifier (timeline)
            </button>
          )}
          <button type="button" onClick={start} disabled={!contentId || !videoUrl}
            title={!contentId ? "Enregistrez d'abord le contenu" : !videoUrl ? 'Ajoutez une vidéo au contenu' : "Le système visionne et écoute le contenu puis propose les filtres à la seconde près"}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold rounded-lg transition-colors ${(!contentId || !videoUrl) ? 'opacity-40 cursor-not-allowed bg-purple-500/10 text-purple-400' : 'bg-purple-500/15 text-purple-500 hover:bg-purple-500/25'}`}
            data-testid="ai-filter-analyze-btn">
            <Sparkles size={10} /> {st?.status === 'done' ? "Relancer l'analyse IA" : 'Analyse IA'}
          </button>
        </>
      )}
    </div>
  );
}

const MEDIA_FIELD_LABELS = { thumbnail_url: 'Vignette', banner_url: 'Bannière', logo_url: 'Logo', video_url: 'Vidéo', trailer_url: 'Bande-annonce', hero_url: 'Image hero', poster_url: 'Affiche', mobile_banner_url: 'Bannière mobile', tv_banner_url: 'Bannière TV', tv_screensaver_url: 'Écran de veille' };

// Ctrl+Z / Cmd+Z restores the previous media after an accidental re-upload
const REQUIRED_MEDIA = [
  ['poster_url', 'Affiche'], ['banner_url', 'Bannière'], ['hero_url', 'Image hero'],
  ['logo_url', 'Logo titre'], ['thumbnail_url', 'Vignette'], ['mobile_banner_url', 'Bannière mobile'],
  ['tv_banner_url', 'Bannière TV'], ['tv_screensaver_url', 'Écran de veille'],
  ['trailer_url', 'Bande-annonce'], ['video_url', 'Vidéo'],
];
const missingMedia = (item) => REQUIRED_MEDIA
  .filter(([f]) => !((item.type === 'series' || item.type === 'game') && f === 'video_url')
    && !(item.type === 'game' && f === 'trailer_url'))
  .filter(([f]) => !item[f]).map(([, l]) => l);

export const MissingMediaBadge = ({ item, className = '' }) => {
  const miss = missingMedia(item);
  if (!miss.length) return null;
  return (
    <span title={`Manquant : ${miss.join(', ')}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/90 text-black cursor-help ${className}`}
      data-testid={`missing-media-badge-${item.id}`}>
      <AlertTriangle size={9} /> {miss.length} manquant{miss.length > 1 ? 's' : ''}
    </span>
  );
};

export function useMediaUndo(setFormFn) {
  const stack = useRef([]);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || (e.key || '').toLowerCase() !== 'z' || e.shiftKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      const last = stack.current.pop();
      if (!last) return;
      e.preventDefault();
      setFormFn(prev => ({ ...prev, [last.field]: last.value }));
      setNotice(`${MEDIA_FIELD_LABELS[last.field] || last.field} : média précédent restauré`);
      setTimeout(() => setNotice(''), 3000);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const push = (field, value) => { if (value) stack.current.push({ field, value }); };
  const noticeEl = notice ? (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}
      className="px-4 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold shadow-xl" data-testid="media-undo-toast">{notice}</div>
  ) : null;
  return { push, noticeEl };
}
